import classNames from 'classnames';
import { useRef } from 'react';
import noop from 'lodash/noop';

import { useOutsideClick } from '../../../global/hooks/use-outside-click';
import { sanitizeHtml } from '../../../helpers/sanitize';
import cloudinary from '../../../helpers/cloudinary';

import Close from '../icon/components/close';

import styles from './cta-tooltip.module.scss';
import ComponentAnimation, {
  AnimationTypes,
} from '../../general/component-animation/component-animation';

type Props = {
  id?: string;
  data: {
    title: string;
    icon?: {
      url?: string;
    };
  };
  setTitleAsInnerHTML?: boolean;
  fallbackIcon?: () => React.ReactElement;
  dismissTooltip?: () => void;
  dismissOnOutsideClick?: boolean;
  noBorderRadius?: boolean;
  hasDarkBackground?: boolean;
  hasCenteredText?: boolean;
  isSmall?: boolean;
  hasSmallPaddings?: boolean;
  arrowClassName?: string;
  textClassName?: string;
  animationDuration?: number;
};

const CtaTooltip = ({
  id,
  data,
  setTitleAsInnerHTML,
  fallbackIcon: FallbackIcon,
  dismissTooltip,
  dismissOnOutsideClick,
  noBorderRadius,
  hasDarkBackground,
  hasCenteredText,
  isSmall,
  hasSmallPaddings,
  arrowClassName,
  textClassName,
  animationDuration = 500,
}: Props) => {
  const tooltipRef = useRef(null);
  useOutsideClick(
    tooltipRef,
    dismissOnOutsideClick && dismissTooltip ? dismissTooltip : noop,
  );

  return (
    <ComponentAnimation
      type={AnimationTypes.EXPAND_DOWN_AND_FADE_IN}
      id='cta-tooltip-animation'
      duration={animationDuration / 1000}
    >
      {!!data && (
        <div
          className={classNames(
            styles['cta-tooltip'],
            arrowClassName && styles['cta-tooltip__arrow'],
            arrowClassName && styles[arrowClassName],
            hasDarkBackground && styles['cta-tooltip--dark-background'],
            arrowClassName &&
              hasDarkBackground &&
              styles['cta-tooltip__arrow--dark-background'],
            noBorderRadius && styles['cta-tooltip--no-border-radius'],
          )}
          ref={tooltipRef}
        >
          <div
            className={classNames(
              styles['cta-tooltip__content'],
              isSmall && styles['cta-tooltip__content--small'],
              hasSmallPaddings &&
                styles['cta-tooltip__content--small-paddings'],
            )}
          >
            <div className={styles['cta-tooltip__icon-container']}>
              {data.icon && data.icon.url ? (
                <div className={styles['cta-tooltip__icon']}>
                  <img
                    className='cta-tooltip__original-icon'
                    src={cloudinary(data.icon.url, 'w_24,h_24', true)}
                  />
                </div>
              ) : (
                FallbackIcon && (
                  <div className={styles['cta-tooltip__icon']}>
                    <FallbackIcon />
                  </div>
                )
              )}
            </div>
            <div
              className={classNames(
                styles['cta-tooltip__text-container'],
                hasCenteredText &&
                  styles['cta-tooltip__text-container--centered-text'],
                isSmall && styles['cta-tooltip__text-container--small'],
                hasSmallPaddings &&
                  styles['cta-tooltip__text-container--small-paddings'],
              )}
            >
              {setTitleAsInnerHTML ? (
                <p
                  id='tooltipText'
                  className={classNames(
                    styles['cta-tooltip__text'],
                    isSmall && styles['cta-tooltip__text--small'],
                    textClassName && styles[textClassName],
                  )}
                  dangerouslySetInnerHTML={
                    setTitleAsInnerHTML
                      ? { __html: sanitizeHtml(data.title) }
                      : undefined
                  }
                />
              ) : (
                <p
                  id='tooltipText'
                  className={classNames(
                    styles['cta-tooltip__text'],
                    isSmall && styles['cta-tooltip__text--small'],
                  )}
                >
                  {data.title}
                </p>
              )}
            </div>
            {!!dismissTooltip &&
              (!isSmall || (isSmall && context.viewport.isMobile)) && (
                <div
                  className={classNames(
                    styles['cta-tooltip__close-container'],
                    id !== 'care-now' &&
                      styles['cta-tooltip__dismiss-button-margin'],
                  )}
                >
                  <button
                    className={styles['cta-tooltip__close']}
                    onClick={dismissTooltip}
                    aria-label={polyglot.t('cta_tooltip.aria.close_tooltip')}
                    aria-describedby='tooltipText'
                  >
                    <Close />
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
    </ComponentAnimation>
  );
};

export default CtaTooltip;
